import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, LinearProgress, Stack } from '@mui/material';
import { classes } from '../styles';
import InfoIcon from '@mui/icons-material/Info';
import Lottie from 'react-lottie';
import felixLoading from '../Graphics/felix-loading.json';
import felixLoadingChart from '../Graphics/felix-reporting.json';
import { generateLoadingText } from '../../AIStudioModal/config';
import { styles } from '../../../NewUI/AgenticMatching/styles';

const LoadingResponse = ({
  generationType,
  showSocketMessage,
  customMessages,
  showProgressBar = false,
  progress = 0,
  showProgress = false,
  total = 0,
  completed = 0
}: {
  generationType: string;
  showSocketMessage?: boolean;
  customMessages?: string[];
  showProgressBar?: boolean;
  progress?: number;
  showProgress?: boolean;
  total?: number;
  completed?: number;
}) => {
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const changeTextIndex = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadingAnimation = {
    loop: true,
    autoplay: true,
    animationData: showProgressBar ? felixLoadingChart : felixLoading,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  const loadingText = useMemo(() => {
    if (generationType === 'custom' && customMessages) {
      return customMessages;
    }
    return generateLoadingText(generationType);
  }, [customMessages, generationType]);

  useEffect(() => {
    changeTextIndex.current = setTimeout(() => {
      if (loadingTextIndex < loadingText.length - 1) {
        setLoadingTextIndex((prev) => prev + 1);
      } else {
        setLoadingTextIndex(0);
      }
    }, 5000);
    return () => {
      if (changeTextIndex.current) {
        clearTimeout(changeTextIndex.current);
      }
    };
  }, [loadingText.length, loadingTextIndex]);

  return (
    <Box sx={classes.loadingContainer}>
      <Lottie options={loadingAnimation} height={500} width={220} />
      <Box>
        <Box sx={classes.loadingMessage}>
          {loadingText[loadingTextIndex as keyof typeof loadingText]}
        </Box>
        {showProgressBar && showProgress && (
          <Stack
            sx={{
              ...styles.fadedFull,
              opacity: 1,
              width: '300px',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <em
              style={{
                opacity: 1,
                transition: 'opacity 0.3s ease-in-out',
                color: '#6E6E6E'
              }}
            >
              {completed > 0 && `Downloading ${completed} of ${total} files`}
            </em>
            <Box sx={styles.progressWrapper}>
              <LinearProgress variant="determinate" value={progress} sx={styles.progressBar} />
            </Box>
          </Stack>
        )}
      </Box>
      <Box
        sx={{
          ...classes.socketsMessage,
          ...(showSocketMessage && { opacity: '1px' }),
          marginTop: '20px'
        }}
      >
        <InfoIcon sx={classes.socketsInfoIcon} />
        <span>
          You can close this at anytime and Felix will let you know when your content has been
          generated.
        </span>
      </Box>
    </Box>
  );
};

export default LoadingResponse;
